/**
 * Switching an enhanced aeroplane between its LOD rungs by distance.
 *
 * ## Where the numbers come from
 *
 * The generator states the ladder once
 * (`tools/blender/rb_enhanced/detail.py`) in the unit the original's own `.TBL`
 * ladder is stated in — models-tbl.md §3's `detail`, the object's apparent
 * **diameter** in pixels of a 200-line frame — and `bun run enhanced` copies it
 * into the manifest. So the app switches on the same quantity a baked model
 * switches on, through the same projection (`sprite-screen-size.ts`), and the
 * three coarse thresholds are literally a `.TBL` fighter's own: 25, 9 and the
 * floor. The fourth, 64, is the rung boundary the enhanced model needs because
 * it carries structure no baked LOD ever had.
 *
 * ## Why `THREE.LOD` rather than a per-frame chooser
 *
 * The renderer already does this. `Renderer.projectObject` calls
 * `object.update(camera)` on every `LOD` it walks, before culling and before
 * the render list is built, which means every camera in the game — chase,
 * fly-by, cockpit, and the kill camera, which frames an aeroplane the flight
 * loop is not following — picks its own rung with no code in the frame loop and
 * nothing for a new camera to remember to call. `LOD.copy` clones its levels,
 * so `combat-fleet.ts`'s `clone()` per participant keeps working and every clone
 * still shares one geometry and one material per rung.
 *
 * The one thing `THREE.LOD` measures differently is *euclidean* distance to the
 * object's origin where `apparentSizePx` uses view-space depth. They differ only
 * off the centre of a wide screen, by the cosine of the off-axis angle, and the
 * consequence is one rung's worth of detail on an aeroplane at the edge of the
 * frame. Taking the projection's own depth would mean re-implementing the walk
 * the renderer already does.
 *
 * ## Popping
 *
 * Each level is added with `LOD_HYSTERESIS`, three.js's own fraction-of-distance
 * dead band: a rung that is *already* showing holds on until the camera is 12%
 * further out than the distance that would have selected it. Without it an
 * aeroplane hovering on a boundary — which is what a co-speed opponent does —
 * alternates rungs every few frames. With it, a crossing happens once.
 */

import * as THREE from "three/webgpu";
import { depthForApparentSize } from "./sprite-screen-size.ts";

/**
 * The ladder as the generator wrote it: one entry per rung, finest first, with
 * the apparent size in 200-line-frame pixels at and above which it is drawn.
 */
export interface EnhancedRung {
  readonly index: number;
  readonly apparentPx: number;
}

/**
 * What the app uses when the manifest carries no ladder — a `data/enhanced`
 * built before the ladder existed, or a run that built only scenery. It is a
 * copy of `detail.py`'s thresholds and exists **only** as that fallback; a
 * manifest that has the field always wins, which is what keeps the generator
 * the single source of truth.
 */
export const DEFAULT_ENHANCED_LADDER: readonly EnhancedRung[] = [
  { index: 0, apparentPx: 64 },
  { index: 1, apparentPx: 25 },
  { index: 2, apparentPx: 9 },
  { index: 3, apparentPx: 0 },
];

/** Fraction of a switching distance a rung already on screen holds on for. */
export const LOD_HYSTERESIS = 0.12;

/** Reads the ladder out of a fetched manifest, falling back when it carries none. */
export function ladderOf(rungs: readonly EnhancedRung[] | undefined): readonly EnhancedRung[] {
  if (!rungs || rungs.length === 0) return DEFAULT_ENHANCED_LADDER;
  return [...rungs].sort((a, b) => a.index - b.index);
}

/**
 * The camera distances the rungs of a `boundingRadius`-sized object switch at,
 * in rung order — the arguments `THREE.LOD.addLevel` wants.
 *
 * The first is always 0 (the finest rung is what a camera on top of the object
 * sees) and each later one is the distance at which the *previous* rung's
 * threshold stops being met. A ladder longer than the model has rungs is
 * truncated by the caller, not here.
 */
export function lodDistancesM(
  ladder: readonly EnhancedRung[],
  boundingRadiusM: number,
  fovYRadians: number,
): number[] {
  return ladder.map((_rung, index) => {
    if (index === 0) return 0;
    const previous = ladder[index - 1] as EnhancedRung;
    const distance = depthForApparentSize(boundingRadiusM, previous.apparentPx, fovYRadians);
    return Number.isFinite(distance) ? distance : 0;
  });
}

/**
 * The rungs of one loaded enhanced model, hung off a `THREE.LOD` that switches
 * between them.
 *
 * Returns the single rung untouched when there is only one — a one-level `LOD`
 * would be an object that walks a list of one every frame to reach the same
 * answer, and scenery and every pre-ladder `.glb` are exactly that case.
 */
export function enhancedLodOf(
  rungs: readonly THREE.Object3D[],
  ladder: readonly EnhancedRung[],
  boundingRadiusM: number,
  fovYRadians: number,
): THREE.Object3D {
  const first = rungs[0];
  if (!first) throw new Error("enhanced model has no LOD rungs");
  if (rungs.length === 1) return first;
  const usable = ladder.slice(0, rungs.length);
  const distances = lodDistancesM(usable, boundingRadiusM, fovYRadians);
  const lod = new THREE.LOD();
  for (const [index, rung] of rungs.entries()) {
    lod.addLevel(rung, distances[index] ?? 0, index === 0 ? 0 : LOD_HYSTERESIS);
  }
  return lod;
}
